"use client";

import { useEffect, useRef } from "react";

/**
 * A cloud of marks that follows the pointer across its parent, the way
 * terminal-styled sites draw particle trails: dense under the cursor,
 * thinning to a scatter behind it.
 *
 * It draws only while the pointer is over the parent and for a moment
 * after it leaves, on a canvas that sits under the parent's content, so
 * the content stays legible and the cloud slips behind it. The parent must
 * be positioned. Nothing runs for readers who prefer reduced motion, and
 * nothing runs at all until a pointer arrives.
 *
 * The field is a sum of soft blobs: one under the pointer and one per
 * recent position, each fading with age. What a cell shows comes from the
 * field value at its centre, and anything below a floor is left blank,
 * which is what makes the edge a sparse scatter rather than a soft disc.
 */
type Variant = "ascii" | "blocks";

type Look = {
  /** Cell size. Characters sit in a tall cell, squares in a square one. */
  w: number;
  h: number;
  /** Core radius in px; the sparse halo reaches about three times this. */
  headR: number;
  /** How long a trail point lasts, and how strong it starts. */
  trailMs: number;
  trail: number;
  /** How often the shimmer re-rolls, and how much it moves a cell. */
  tickMs: number;
  jitter: number;
  /**
   * The chance a cell is drawn: `drop[0] + drop[1] * field`, capped at
   * `keep`, the most cells drawn right under the pointer.
   */
  drop: [number, number];
  keep: number;
  /**
   * How fast what is drawn follows the field, per frame. 1 is instant and
   * lively; lower values ease, so cells grow and fade instead of popping.
   */
  ease: number;
  /** Whether a cell's chance of being drawn is fixed, or re-rolled each tick. */
  stable: boolean;
};

const LOOK: Record<Variant, Look> = {
  // Cream characters on a coloured panel: a lively comet.
  ascii: { w: 10, h: 15, headR: 46, trailMs: 800, trail: 0.45, tickMs: 110, jitter: 1, drop: [0.08, 0.6], keep: 0.8, ease: 1, stable: false },
  // Brand-red squares on a light surface: a fifth of the area, a short tail,
  // and a slow, gentle shimmer that eases rather than flickers. Fuller than
  // the characters within its small reach, so a dozen marks still read as
  // one cloud rather than stray pixels.
  blocks: { w: 12, h: 12, headR: 21, trailMs: 520, trail: 0.5, tickMs: 420, jitter: 0.35, drop: [0.2, 0.7], keep: 0.9, ease: 0.28, stable: true },
};

const RAMP = [".", ":", "-", "+", "*", "#", "%"];
const MAX_POINTS = 40;
const FLOOR = 0.08;

/**
 * Elements whose box counts as "text" for the site-wide layer. A cell that
 * sits under one of these is drawn at a fraction of its strength, so the
 * cloud recedes behind a headline or a paragraph instead of competing with
 * the letters. Hit-testing skips the layer itself, which takes no pointer
 * events, and lands on whatever is painted at that point.
 */
const TEXT = "h1,h2,h3,h4,h5,h6,p,li,a,button,label,blockquote,dt,dd,th,td,pre,code,small,strong,em,b,i,span,figcaption,summary,legend,input,textarea,select";
const UNDER_TEXT = 0.3;
/** How often the under-text map is rebuilt, in ms. */
const TEXT_MAP_MS = 150;

type Pt = { x: number; y: number; t: number };

/** Cheap, deterministic noise in [0, 1) for a cell and a tick. */
const noise = (a: number, b: number, c: number) => {
  const s = Math.sin(a * 12.9898 + b * 78.233 + c * 37.719) * 43758.5453;
  return s - Math.floor(s);
};

/**
 * `ascii` is cream characters for a dark or coloured panel. `blocks` is for
 * a light surface: brand-red squares that grow with the field, with one
 * cell in ten drawn as a `#` and, when a `mark` image is given, another one
 * in ten drawn as that mark.
 */
export function AsciiTrail({
  className,
  variant = "ascii",
  mark,
  global = false,
}: {
  className?: string;
  variant?: Variant;
  /** URL of a small image to scatter through the cloud, blocks variant only. */
  mark?: string;
  /**
   * Follow the pointer anywhere on the page rather than only over the
   * parent. The parent should then be a fixed, full-viewport layer. Any
   * element marked `data-trail="off"` is left alone: the cloud fades out
   * while the pointer is over it.
   */
  global?: boolean;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const host = canvas?.parentElement;
    if (!canvas || !host) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const L = LOOK[variant];
    const img = variant === "blocks" && mark ? new Image() : null;
    if (img) img.src = mark as string;
    const markReady = () => !!img && img.complete && img.naturalWidth > 0;

    let w = 0;
    let h = 0;
    let cols = 0;
    let rows = 0;
    /** The field as it is now, and the field as it is drawn, which eases towards it. */
    let field = new Float32Array(0);
    let shown = new Float32Array(0);
    const points: Pt[] = [];
    let head: Pt | null = null;
    let inside = false;
    let raf = 0;

    const resize = () => {
      const r = host.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.round(r.width);
      h = Math.round(r.height);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = `${variant === "blocks" ? 700 : 600} ${variant === "blocks" ? 10 : 12}px ${getComputedStyle(document.documentElement).getPropertyValue("--font-mono") || "monospace"}`;
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";
      cols = Math.ceil(w / L.w);
      rows = Math.ceil(h / L.h);
      field = new Float32Array(cols * rows);
      shown = new Float32Array(cols * rows);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(host);
    resize();

    /**
     * Is this point of the page covered by text? Only asked for the
     * site-wide layer, whose canvas shares the viewport's coordinates, and
     * only for cells that are about to be drawn, so it is a few dozen
     * hit-tests every TEXT_MAP_MS rather than thousands a frame.
     */
    const underText = (x: number, y: number): boolean => {
      const el = document.elementFromPoint(x, y);
      if (!el) return false;
      if (el.closest(TEXT)) return true;
      for (const n of el.childNodes) if (n.nodeType === 3 && n.textContent && n.textContent.trim()) return true;
      return false;
    };
    const textMap = new Map<number, number>();
    let textTick = -1;

    /** Add one blob to the field: a tight core plus a wide, weak halo. */
    const splat = (p: Pt, strength: number) => {
      if (strength <= 0) return;
      const r = L.headR * (0.55 + 0.45 * strength);
      const reach = r * 3;
      const c0 = Math.max(0, Math.floor((p.x - reach) / L.w));
      const c1 = Math.min(cols - 1, Math.ceil((p.x + reach) / L.w));
      const r0 = Math.max(0, Math.floor((p.y - reach) / L.h));
      const r1 = Math.min(rows - 1, Math.ceil((p.y + reach) / L.h));
      for (let row = r0; row <= r1; row++) {
        const dy = (row * L.h + L.h / 2 - p.y) / r;
        for (let col = c0; col <= c1; col++) {
          const dx = (col * L.w + L.w / 2 - p.x) / r;
          const d2 = dx * dx + dy * dy;
          if (d2 > 9) continue;
          field[row * cols + col] += strength * (Math.exp(-d2 * 1.6) + 0.16 * Math.exp(-d2 * 0.25));
        }
      }
    };

    const draw = (now: number) => {
      field.fill(0);
      while (points.length && now - points[0].t > L.trailMs) points.shift();
      for (const p of points) {
        const age = (now - p.t) / L.trailMs;
        splat(p, (1 - age) * L.trail);
      }
      if (head && inside) splat(head, 1);

      if (L.ease >= 1) {
        shown.set(field);
      } else {
        for (let i = 0; i < shown.length; i++) shown[i] += (field[i] - shown[i]) * L.ease;
      }

      ctx.clearRect(0, 0, w, h);
      const tick = Math.floor(now / L.tickMs);
      const mapTick = Math.floor(now / TEXT_MAP_MS);
      if (mapTick !== textTick) {
        textTick = mapTick;
        textMap.clear();
      }
      let drew = false;
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const v = shown[row * cols + col];
          if (v < 0.03) continue;
          // Most cells stay blank even near the core. The chance of drawing
          // rises with the field, so the cloud is gappy everywhere and only
          // crowds together right under the pointer.
          if (noise(col * 3 + 1, row * 7 + 2, L.stable ? 0.5 : tick) > Math.min(L.keep, L.drop[0] + v * L.drop[1])) continue;
          const s = v * (1 - L.jitter / 2 + L.jitter * noise(col, row, tick));
          if (s < FLOOR) continue;
          drew = true;
          const x = col * L.w + L.w / 2;
          const y = row * L.h + L.h / 2;

          // Under text, the cell recedes.
          let under = 1;
          if (global) {
            const i = row * cols + col;
            const cached = textMap.get(i);
            if (cached === undefined) {
              under = underText(x, y) ? UNDER_TEXT : 1;
              textMap.set(i, under);
            } else {
              under = cached;
            }
          }

          if (variant === "ascii") {
            const idx = Math.min(RAMP.length - 1, Math.floor(s * 4));
            ctx.fillStyle = `rgba(255, 244, 236, ${Math.min(0.95, 0.25 + s * 0.6) * under})`;
            ctx.fillText(RAMP[idx], x, y);
            continue;
          }

          // Blocks. What a cell is stays fixed for that cell, so the marks
          // and hashes do not flicker between kinds as the cloud moves, and
          // a cell just past the floor comes in faint rather than popping.
          const kind = noise(col * 5 + 3, row * 11 + 7, 0.5);
          const fade = Math.min(1, (s - FLOOR) / 0.1);
          const a = Math.min(0.85, 0.18 + s * 0.5) * (0.35 + 0.65 * fade) * under;
          if (kind < 0.1 && markReady()) {
            ctx.globalAlpha = a;
            ctx.drawImage(img as HTMLImageElement, x - 5, y - 5, 10, 10);
            ctx.globalAlpha = 1;
          } else if (kind < 0.2) {
            ctx.fillStyle = `rgba(28, 24, 20, ${a})`;
            ctx.fillText("#", x, y);
          } else {
            const size = 3 + Math.min(1.4, s) * 4;
            ctx.fillStyle = `rgba(240, 90, 40, ${a})`;
            ctx.beginPath();
            ctx.roundRect(x - size / 2, y - size / 2, size, size, 1.5);
            ctx.fill();
          }
        }
      }

      if (drew || inside) {
        raf = requestAnimationFrame(draw);
      } else {
        raf = 0;
        shown.fill(0);
      }
    };

    const start = () => {
      if (!raf) raf = requestAnimationFrame(draw);
    };
    const local = (e: PointerEvent): Pt => {
      const r = host.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top, t: performance.now() };
    };

    const onLeave = () => {
      if (!inside && !head) return;
      inside = false;
      // The core becomes the newest trail point, so it fades out with the
      // rest instead of vanishing.
      if (head) points.push({ ...head, t: performance.now() });
      head = null;
      start();
    };
    const offZone = (e: PointerEvent) => global && e.target instanceof Element && !!e.target.closest('[data-trail="off"]');
    const onEnter = (e: PointerEvent) => {
      if (e.pointerType === "touch" || offZone(e)) return;
      inside = true;
      head = local(e);
      start();
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (offZone(e)) {
        onLeave();
        return;
      }
      const p = local(e);
      inside = true;
      head = p;
      const last = points[points.length - 1];
      if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 6) {
        points.push(p);
        if (points.length > MAX_POINTS) points.shift();
      }
      start();
    };

    // Over the parent only, or over the whole page: the page version hears
    // every move through the window and treats leaving the document, or the
    // window losing focus, as the pointer leaving.
    const root = document.documentElement;
    if (global) {
      window.addEventListener("pointermove", onMove);
      root.addEventListener("pointerleave", onLeave);
      window.addEventListener("blur", onLeave);
    } else {
      host.addEventListener("pointerenter", onEnter);
      host.addEventListener("pointermove", onMove);
      host.addEventListener("pointerleave", onLeave);
    }
    return () => {
      window.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("blur", onLeave);
      host.removeEventListener("pointerenter", onEnter);
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerleave", onLeave);
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [variant, mark, global]);

  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
